import { useState, useMemo, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  ChefHat,
  Boxes,
  Users,
  BarChart3,
  Settings,
  LogOut,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  Shield,
  History,
  Command,
  Wrench,
  Wifi,
  WifiOff,
  MonitorPlay,
  BookOpen,
  LayoutGrid,
  User,
  Receipt,
  MessageSquarePlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOffline } from '@/hooks/useOffline';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAppearanceSettings } from '@/hooks/useAppearanceSettings';
import { useModuleSettings, type ModuleKey } from '@/hooks/useModuleSettings';
import { useIndustryTerms } from '@/hooks/useIndustryTerms';
import { StatusDialog } from '@/components/StatusDialog';
import { FeedbackModal } from '@/components/FeedbackModal';
import { useTrainingModeStore } from '@/store/trainingMode';
import { toast } from 'sonner';

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  permission?: string | string[];
  requireAll?: boolean;
  superadminOnly?: boolean;
  excludeRoles?: string[];
  moduleKey?: ModuleKey | ModuleKey[];
  end?: boolean; // If true, NavLink uses exact matching (prevents parent highlight on child routes)
  tourId?: string; // ID for onboarding tour targeting
}

// Display names for roles
const ROLE_DISPLAY_NAMES: Record<string, string> = {
  superadmin: 'Super Admin',
  primary_admin: 'Business Owner',
  owner: 'Owner',
  admin: 'Administrator',
  manager: 'Manager',
  lead_cashier: 'Lead Cashier',
  cashier: 'Cashier',
  employee: 'Employee',
  inventory_manager: 'Inventory Manager',
  inventory_supervisor: 'Inventory Supervisor',
  inventory_clerk: 'Inventory Clerk',
  accountant: 'Accountant',
};

const formatRoleDisplay = (role: string | undefined): string => {
  if (!role) return 'Unknown';
  return ROLE_DISPLAY_NAMES[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

interface NavSection {
  title?: string; // Header text (e.g., "OPERATIONS")
  items: NavItem[];
}

// Define navigation sections with their items
// Visibility is controlled by permissions + organization module settings
const navSections: NavSection[] = [
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, tourId: 'tour-dashboard-link' },
      { label: 'Morning Brief', to: '/morning-brief', icon: Sun, permission: 'reports.view_sales', moduleKey: 'reports' },
      { label: 'Sell', to: '/sell', icon: ShoppingCart, permission: 'pos.access', moduleKey: 'pos', tourId: 'tour-sell-link' },
      { label: 'Floor Plan', to: '/floor-plan', icon: LayoutGrid, permission: 'pos.access', moduleKey: 'floorPlan' },
      { label: 'Order History', to: '/sell/sales-history', icon: Receipt, permission: 'orders.view' },
    ],
  },
  {
    title: 'KITCHEN',
    items: [
      { label: 'Kitchen (KDS)', to: '/kds', icon: ChefHat, permission: 'orders.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'kds' },
      { label: 'KDS Stations', to: '/kitchen-stations', icon: MonitorPlay, permission: 'settings.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'kdsStations' },
    ],
  },
  {
    title: 'CATALOG',
    items: [
      { label: 'Inventory', to: '/stock-control', icon: Boxes, permission: ['inventory.view', 'menu.view'], moduleKey: ['inventory', 'products'], tourId: 'tour-products-link' },
      { label: 'Standardization', to: '/standardization', icon: BookOpen, permission: 'menu.view', moduleKey: 'standardization' },
    ],
  },
  {
    title: 'CUSTOMERS',
    items: [
      { label: 'Customers', to: '/customers', icon: Users, permission: 'customers.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'customers' },
    ],
  },
  {
    title: 'INSIGHTS',
    items: [
      { label: 'Reports', to: '/reports', icon: BarChart3, permission: 'reports.view_sales', moduleKey: 'reports' },
      { label: 'Audit Logs', to: '/audit-logs', icon: History, permission: 'audit.view', moduleKey: 'auditLogs' },
    ],
  },
  {
    title: 'ADMIN',
    items: [
      { label: 'Command Center', to: '/command-center', icon: Command, permission: 'backoffice.access', moduleKey: 'commandCenter' },
      { label: 'Settings', to: '/settings', icon: Settings, permission: 'settings.view', tourId: 'tour-settings-link' },
      { label: 'Team', to: '/team', icon: Shield, permission: 'team.view', moduleKey: 'team' },
      { label: 'Utilities', to: '/utilities', icon: Wrench, superadminOnly: true, moduleKey: 'utilities' },
    ],
  },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { signOut, profile, user, loading } = useAuth();
  const { setTheme } = useTheme();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const { isOnline, pendingCount, failedCount } = useOffline();
  const unsyncedCount = pendingCount + failedCount;
  const { flags } = useFeatureFlags();
  const { isModuleEnabled, subscriptionLoading } = useModuleSettings();
  const { updateUserTheme } = useAppearanceSettings();
  const terms = useIndustryTerms();
  const navigate = useNavigate();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const { isTrainingMode } = useTrainingModeStore();

  // Apply industry-specific labels to navigation sections
  const dynamicNavSections = useMemo(() => {
    return navSections.map(section => {
      if (section.title === 'KITCHEN') {
        return {
          ...section,
          title: terms.kitchenSection,
          items: section.items.map(item => {
            if (item.to === '/kds') return { ...item, label: terms.kitchenDisplay };
            if (item.to === '/kitchen-stations') return { ...item, label: terms.kitchenStations };
            return item;
          }),
        };
      }
      if (section.title === 'OPERATIONS') {
        return {
          ...section,
          items: section.items.map(item => {
            if (item.to === '/floor-plan') return { ...item, label: terms.floorPlan };
            return item;
          }),
        };
      }
      return section;
    });
  }, [terms]);

  // Handle theme change - update UI immediately and persist to database
  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    setTheme(theme); // Immediate UI update
    updateUserTheme.mutate(theme); // Persist to database
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Failed to sign out:', error);
      // Force navigation to login even if there was an error
      navigate('/login', { replace: true });
    }
  };

  // Track previous sections to prevent flickering during loading
  const previousSectionsRef = useRef<{ title?: string; items: NavItem[] }[]>([]);

  // Helper function to check if an item is visible based on permissions
  const isItemVisible = (item: NavItem): boolean => {
    // Check superadmin-only items first
    if (item.superadminOnly) {
      return profile?.role === 'superadmin';
    }

    // Check if user's role is excluded from this item
    if (item.excludeRoles && profile?.role && item.excludeRoles.includes(profile.role)) {
      return false;
    }

    // Check module visibility (organization-level)
    if (item.moduleKey) {
      const moduleKeys = Array.isArray(item.moduleKey) ? item.moduleKey : [item.moduleKey];
      if (!moduleKeys.some((moduleKey) => isModuleEnabled(moduleKey))) {
        return false;
      }
    }

    if (!item.permission) return true;

    const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];

    if (item.requireAll) {
      return hasAllPermissions(permissions);
    }

    return permissions.length === 1
      ? hasPermission(permissions[0])
      : hasAnyPermission(permissions);
  };

  // Filter sections based on permissions - only show sections with at least one visible item
  const visibleSections = useMemo(() => {
    // If still loading and we have no profile yet, return previous sections to prevent flicker
    if ((loading || subscriptionLoading) && previousSectionsRef.current.length > 0) {
      return previousSectionsRef.current;
    }

    const filtered = dynamicNavSections
      .map(section => {
        const items = section.items.filter(isItemVisible);
        return {
          title: section.title,
          items,
        };
      })
      .filter(section => section.items.length > 0);

    // Store the filtered sections for future reference
    if (filtered.length > 0) {
      previousSectionsRef.current = filtered;
    }

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, loading, subscriptionLoading, hasPermission, hasAnyPermission, hasAllPermissions, isModuleEnabled]);

  return (
    <aside
      className={cn(
        'group relative flex h-full flex-col border-r backdrop-blur-xl transition-all duration-200 overflow-hidden',
        'bg-gray-200 text-gray-800 dark:bg-card/80 dark:text-foreground',
        'pattern-dots',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className={cn(
        "flex h-[72px] items-center gap-3 border-b border-gray-300 dark:border-border transition-all",
        collapsed ? "justify-center px-2" : "px-6"
      )}>
        {/* Always show BlazePOS logo */}
        <div className={cn(
          "flex shrink-0 items-center justify-center rounded-2xl overflow-hidden transition-all",
          collapsed ? "h-7 w-7" : "h-10 w-10"
        )}>
          <img src="/blazepos-logo.png" alt="BlazePOS" className={cn(
            "object-contain transition-all",
            collapsed ? "h-7 w-7" : "h-10 w-10"
          )} />
        </div>
        {!collapsed && (
          <div className="flex flex-col flex-1">
            {/* Always show BlazePOS branding */}
            <span className="font-display text-xl font-bold tracking-tight blaze-gradient-text">
              BlazePOS
            </span>
          </div>
        )}
        {/* Collapse toggle button - integrated in header */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "hidden h-7 w-7 shrink-0 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-300/50 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-muted/70 md:flex",
            collapsed && "ml-0"
          )}
          onClick={onToggle}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      <nav id="tour-sidebar-nav" className="flex-1 space-y-1 px-2 py-4 overflow-y-auto scrollbar-hide">
        {visibleSections.map((section, sectionIndex) => (
          <div key={section.title || sectionIndex} className={cn(sectionIndex > 0 && 'mt-4')}>
            {/* Section header - keep space consistent, hide text when collapsed */}
            {flags.showSidebarSectionHeaders && section.title && (
              <div className={cn(
                "px-3 py-2 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-widest overflow-hidden whitespace-nowrap",
                collapsed && "opacity-0"
              )}>
                {section.title}
              </div>
            )}
            {/* Section divider when headers are disabled but not first section */}
            {!flags.showSidebarSectionHeaders && sectionIndex > 0 && (
              <div className="my-2 border-t border-gray-300 dark:border-border" />
            )}
            {/* Section items */}
            {section.items.map((item) => {
              const Icon = item.icon;

              // In training mode, block navigation to anything except /sell routes
              const isNavBlocked = isTrainingMode && !item.to.startsWith('/sell');

              const handleTrainingBlock = (e: React.MouseEvent) => {
                if (isNavBlocked) {
                  e.preventDefault();
                  toast.error('Navigation Locked', {
                    description: 'Exit Training Mode to navigate to other pages.',
                  });
                }
              };

              if (collapsed) {
                return (
                  <div key={item.to} className="block">
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          onClick={handleTrainingBlock}
                          style={{ display: 'flex', width: '100%' }}
                          className={({ isActive }) =>
                            cn(
                              'group/navitem items-center justify-center rounded-xl px-3 py-2 text-sm font-medium transition-colors min-h-[36px]',
                              'hover:bg-gray-300/50 dark:hover:bg-muted/70',
                              isActive && 'bg-primary/10 shadow-sm shadow-blaze-500/20',
                              isNavBlocked && 'opacity-40 cursor-not-allowed'
                            )
                          }
                        >
                          {({ isActive }) => (
                            <Icon
                              className={cn(
                                'h-4 w-4 shrink-0 transition-colors',
                                isActive
                                  ? 'text-primary'
                                  : 'text-gray-600 group-hover/navitem:text-primary dark:text-muted-foreground dark:group-hover/navitem:text-primary'
                              )}
                            />
                          )}
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {item.label}{isNavBlocked ? ' (locked)' : ''}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  id={item.tourId}
                  onClick={handleTrainingBlock}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors min-h-[36px]',
                      'text-gray-600 hover:text-gray-900 hover:bg-gray-300/50 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-muted/70',
                      isActive && 'bg-primary/10 text-primary shadow-sm shadow-blaze-500/20',
                      isNavBlocked && 'opacity-40 cursor-not-allowed'
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-gray-300 dark:border-border px-3 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'flex w-full items-center gap-2 rounded-xl px-2 py-2 hover:bg-gray-300/50 dark:hover:bg-muted',
                collapsed ? 'justify-center' : 'justify-start'
              )}
            >
              <UserAvatar
                userId={user?.id || ''}
                firstName={profile?.first_name}
                lastName={profile?.last_name}
                role={profile?.role}
                size="sm"
                useVibrantColors={flags.vibrantAvatars}
              />
              {!collapsed && (
                <>
                  <div className="flex flex-1 flex-col items-start overflow-hidden text-left">
                    <span className="w-full truncate text-sm font-medium text-gray-900 dark:text-foreground">
                      {loading ? '...' : `${profile?.first_name || ''} ${profile?.last_name || ''}`}
                    </span>
                    <span className="w-full truncate text-xs text-gray-600 dark:text-muted-foreground">
                      {loading ? 'Loading...' : formatRoleDisplay(profile?.role)}
                    </span>
                  </div>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500 dark:text-muted-foreground" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={collapsed ? 'center' : 'end'}
            side={collapsed ? 'right' : 'top'}
            className="w-56"
            sideOffset={10}
          >
            <DropdownMenuItem 
              onClick={() => {
                if (isTrainingMode) {
                  toast.error('Navigation Locked', {
                    description: 'Exit Training Mode to navigate to other pages.',
                  });
                  return;
                }
                navigate('/settings');
              }}
              className={cn("font-semibold cursor-pointer", isTrainingMode && "opacity-40")}
            >
              <User className="mr-2 h-4 w-4" />
              <span>My Account</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setStatusDialogOpen(true)}>
              <div className="flex items-center">
                {isOnline ? (
                  <Wifi className="mr-2 h-4 w-4 text-green-500" />
                ) : (
                  <WifiOff className="mr-2 h-4 w-4 text-red-500" />
                )}
                <span>Status</span>
              </div>
              {unsyncedCount > 0 && (
                <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                  {unsyncedCount}
                </span>
              )}
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Sun className="mr-2 h-4 w-4" />
                <span>Display</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => handleThemeChange('light')}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>Light</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleThemeChange('dark')}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>Dark</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleThemeChange('system')}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>System</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={() => setFeedbackModalOpen(true)}>
              <MessageSquarePlus className="mr-2 h-4 w-4" />
              <span>Provide Feedback</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleSignOut}
              className="text-destructive focus:text-destructive cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <StatusDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen} />
      <FeedbackModal open={feedbackModalOpen} onOpenChange={setFeedbackModalOpen} />
    </aside>
  );
}
